import { decrypt, encrypt, generateKey } from "./crypto";

type BridgeMessage = {
  type: string;
  payload: any;
  txn: string;
  __source: "host" | "remote";
  __origin?: string;
};

type Listener = (message: BridgeMessage) => void;

interface BridgeOptions {
  allowedHostOrigins: string[];
  cryptoKey?: string;
  debug?: boolean;
  enableNavigationSync?: boolean; // 👈 NEW
}

let isInitialized = false;

let globalConfig: { appId: string; origins: string[] } | null = null;
let globalBridge: ReturnType<typeof setupBridge> | null = null;

export function createBridge(appId: string, options: BridgeOptions) {
  if (globalBridge) {
    if (
      globalConfig?.appId !== appId ||
      JSON.stringify(globalConfig?.origins) !== JSON.stringify(options.allowedHostOrigins)
    ) {
      console.warn("⚠️ Bridge already initialized with different config");
    }

    return globalBridge;
  }

  globalConfig = {
    appId,
    origins: options.allowedHostOrigins
  };

  globalBridge = setupBridge(appId, options);

  return globalBridge;
}

export function resetBridge() {
  globalBridge?.destroy();
  globalBridge = null;
}

export function signalBridge() {
  if (!globalBridge) {
    throw new Error("❌ Bridge belum di-init. Panggil createBridge().init() dulu");
  }

  return globalBridge;
}


export function setupBridge(
  appId: string,
  options: BridgeOptions
) {
  const { allowedHostOrigins, cryptoKey, debug } = options;

  const isRemote = window.parent && window.parent !== window;

  if (!isRemote) {
    throw new Error("Signal bridge hanya boleh dijalankan di iframe (remote).");
  }

  const listeners: Listener[] = [];

  const log = (...args: any[]) => {
    if (debug) console.log("[🛰️DEBUG-SIGNAL-BRIDGE]", ...args);
  };

  log("Initialized (REMOTE)");

  // =====================================================
  // RECEIVE MESSAGE FROM HOST
  // =====================================================
  const messageHandler = (event: MessageEvent) => {
    const data = event.data;
    const origin = event.origin;

    if (!data || typeof data !== "object" || !data.type) return;
    if (!allowedHostOrigins.includes(origin)) return;
    if (data.__source === "remote") return; // anti loop

    let payload = data.payload;

    if (cryptoKey && typeof payload === "string" && payload) {
      payload = decrypt(payload, cryptoKey);
    }

    const remoteMessage: BridgeMessage = {
      type: data.type,
      payload,
      txn: data.txn || "",
      __source: "host",
      __origin: origin,
    };

    listeners.forEach((fn) => fn(remoteMessage));
  };

  window.addEventListener("message", messageHandler);

  // =====================================================
  // SEND MESSAGE TO HOST
  // =====================================================
  function emitToHost(type: string, payload: any, txn?: string) {
    let finalPayload = payload;

    if (cryptoKey) {
      finalPayload = encrypt(payload, cryptoKey);
    }

    const message = {
      app_id: appId,
      type,
      payload: finalPayload,
      txn: txn || "",
      __origin: window.location.origin,
      __source: "remote",
    };

    if (window.parent && window.parent !== window) {
      allowedHostOrigins.forEach((origin) => {
        window.parent!.postMessage(message, origin);
      });
    }
  }

  // =====================================================
  // NAVIGATION SYNC (SPA SAFE)
  // =====================================================


  // initial sync
  function emitReadyOnce() {
    if (isInitialized) return;

    isInitialized = true;
    log("READY emitted");

    emitToHost("ready", {
      ok: true,
      app_id: appId,
      timestamp: Date.now()
    });
  }

  // let originalPushState = history.pushState;
  // let originalReplaceState = history.replaceState;

  // let navigationEnabled = false;



  // function setupNavigationSync() {
  //   if (!options.enableNavigationSync) return;

  //   navigationEnabled = true;

  //   const emitCurrentPath = () => {
  //     let path = location.hash.startsWith("#/")
  //       ? location.hash.slice(1)
  //       : location.pathname;

  //     path = path.split("?")[0].split("#")[0];
  //     if (!path.startsWith("/")) path = "/" + path;

  //     log("NAVIGATE", path);

  //     emitToHost("navigate", { path });
  //   };

  //   history.pushState = function (...args) {
  //     originalPushState.apply(history, args as any);
  //     emitCurrentPath();
  //   };

  //   history.replaceState = function (...args) {
  //     originalReplaceState.apply(history, args as any);
  //     emitCurrentPath();
  //   };

  //   window.addEventListener("popstate", emitCurrentPath);
  //   window.addEventListener("hashchange", emitCurrentPath);

  //   // fallback SPA
  //   function waitForSPAPath(maxAttempts = 20, interval = 50) {
  //     let attempts = 0;

  //     const checkPath = () => {
  //       const path = location.hash.startsWith("#/")
  //         ? location.hash.slice(1)
  //         : location.pathname;

  //       if (path !== "/" || attempts >= maxAttempts) {
  //         emitToHost("navigate", { path });
  //       } else {
  //         attempts++;
  //         setTimeout(checkPath, interval);
  //       }
  //     };

  //     checkPath();
  //   }

  //   window.addEventListener("load", () => {
  //     waitForSPAPath();
  //   });
  // }



  // =====================================================
  // PUBLIC API
  // =====================================================
  return {

    init() {
      emitReadyOnce();
    },

    listen(handler: Listener) {
      listeners.push(handler);
    },

    emit(type: string, payload: any) {
      emitToHost(type, payload);
    },

    destroy() {
      window.removeEventListener("message", messageHandler);
    },
  };
}

export function initBridge(appId: string, options: BridgeOptions) {
  const instance = createBridge(appId, options);
  instance.init();
  return instance;
}

export * from "./crypto";

// 👇 UMD / browser global support
if (typeof window !== "undefined") {
  (window as any).signalBridge = Object.assign(signalBridge, {
    init: initBridge, // 👈 pakai ini (auto init)
    createBridge,
    reset: resetBridge,
    encrypt,
    decrypt,
    generateKey
  });
}